import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import { useUpdateVendor, useDeleteVendor } from '../../hooks/useVendor';
import useVendor from '../../hooks/useVendor';

export default function VendorDashboard() {
    const queryClient = useQueryClient();
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [vendorToDelete, setVendorToDelete] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentSearchQuery, setCurrentSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [statusUpdating, setStatusUpdating] = useState({});
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [page, setPage] = useState(1);
    const perPage = 20;

    // Fetch vendors using the useVendor hook
    const { data, isLoading, isError } = useVendor(page, perPage, currentSearchQuery, statusFilter);
    const vendors = data?.data || [];
    const meta = data?.meta || {};
    const totalPages = meta.last_page || 1;

    // Update vendor status mutation using hook
    const { mutate: updateStatus } = useUpdateVendor({
        onSuccess: () => {
            toast.success('Status updated successfully');
            queryClient.invalidateQueries(['vendors']);
        },
        onError: (error, variables) => {
            toast.error(error.message || 'Failed to update status');
            if (variables && variables.id) {
                setStatusUpdating(prev => ({ ...prev, [variables.id]: false }));
            }
        },
        onSettled: (data, error, variables) => {
            // Wait for the vendors query to refetch before hiding the spinner
            queryClient.refetchQueries(['vendors']).then(() => {
                if (variables && variables.id) {
                    setStatusUpdating(prev => ({ ...prev, [variables.id]: false }));
                }
            });
        }
    });

    // Delete vendor mutation using hook (no isLoading, use local state)
    const { mutate: deleteVendor } = useDeleteVendor();

    const handleDeleteConfirm = () => {
        if (!vendorToDelete) return;
        setDeleteLoading(true);
        deleteVendor(vendorToDelete.vendor_id, {
            onSuccess: () => {
                toast.success('Vendor deleted successfully');
                queryClient.invalidateQueries(['vendors']);
            },
            onError: (error) => {
                let message =
                    error?.response?.data?.message ||
                    (Array.isArray(error?.response?.data?.errors) ? error.response.data.errors[0] : undefined) ||
                    error?.message ||
                    'Failed to delete vendor';

                // Check for SQL foreign key constraint error in the 'error' field
                const sqlError = error?.response?.data?.error;
                if (
                    (typeof sqlError === 'string' && (
                        sqlError.includes('SQLSTATE[23000]') ||
                        sqlError.toLowerCase().includes('foreign key constraint fails') ||
                        sqlError.toLowerCase().includes('integrity constraint violation')
                    )) ||
                    message.toLowerCase().includes('foreign key') ||
                    message.toLowerCase().includes('constraint') ||
                    message.toLowerCase().includes('has products') ||
                    message.toLowerCase().includes('has orders')
                ) {
                    message = "This vendor cannot be deleted because they are linked to other records (such as stocks, products, or orders). Please remove those links first.";
                }

                toast.error(message);
            },
            onSettled: () => {
                setDeleteLoading(false);
                setShowDeleteModal(false);
                setVendorToDelete(null);
            }
        });
    };

    const handleDeleteClick = (vendor) => {
        setVendorToDelete(vendor);
        setShowDeleteModal(true);
    };

    const handleStatusChange = (vendor, newStatus) => {
        setStatusUpdating(prev => ({ ...prev, [vendor.vendor_id]: true }));
        updateStatus({ id: vendor.vendor_id, payload: {
            name: vendor.user.name,
            email: vendor.user.email,
            phone_no: vendor.user.phone_no,
            vendor_type: vendor.vendor_type,
            origin: vendor.origin,
            description: vendor.description,
            status: newStatus
        }});
    };

    // Use vendors directly since filtering is now done on server-side
    const filteredVendors = vendors;

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setPage(newPage);
        }
    };

    const handleSearch = () => {
        setCurrentSearchQuery(searchQuery);
        setPage(1); // Reset to first page when searching
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    if (isLoading) {
        return (
            <div className="container-fluid">
                <div className="row">
                    <div className="col-sm-12">
                        <div className="text-center py-4">
                            <div className="spinner-border" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-2">Loading vendors...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (isError) {
        return (
            <div className="container-fluid">
                <div className="row">
                    <div className="col-sm-12">
                        <div className="text-center py-4">
                            <p className="text-danger">Error loading vendors. Please try again.</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="container-fluid">
                <div className="row">
                    <div className="col-sm-12">
                        <div className="title-header option-title d-flex align-items-center justify-content-between mb-4">
                            <h5>All Vendors</h5>
                            <div className="d-flex align-items-center">
                                <input
                                    type="text"
                                    className="form-control form-control-sm me-2"
                                    style={{ width: 220 }}
                                    placeholder="Search by name, email, or ID..."
                                    value={searchQuery}
                                    onChange={e => setSearchQuery(e.target.value)}
                                    onKeyPress={handleKeyPress}
                                />
                                <button
                                    className="btn btn-primary btn-sm me-2"
                                    onClick={handleSearch}
                                    disabled={isLoading}
                                >
                                    {isLoading ? (
                                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                    ) : (
                                        <i className="fas fa-search me-1"></i>
                                    )}
                                    Search
                                </button>
                                <form className="d-inline-flex me-2">
                                    <Link to="/vendoraddorupdate" className="align-items-center btn btn-theme d-flex">
                                        <i data-feather="plus"></i>Add Vendor
                                    </Link>
                                </form>
                                <select
                                    className="form-select form-select-sm w-auto"
                                    value={statusFilter}
                                    onChange={e => {
                                        setStatusFilter(e.target.value);
                                        setPage(1); // Reset to first page when filtering
                                    }}
                                >
                                    <option value="all">All Statuses</option>
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                </select>
                            </div>
                        </div>
                        {filteredVendors.length === 0 ? (
                            <div className="text-center py-4">
                                <p className="text-muted">No vendors found</p>
                            </div>
                        ) : (
                            <>
                            <div className="table-responsive table-product">
                                <table className="table all-package theme-table align-middle" id="table_id">
                                    <thead className="table-light">
                                        <tr>
                                            <th className="text-center">ID</th>
                                            <th className="text-start">Name</th>
                                            <th className="text-start">Email</th>
                                            <th className="text-center">Status</th>
                                            <th className="text-center">Option</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredVendors.map((vendor, idx) => (
                                            <tr key={idx}>
                                                <td className="text-center">{vendor.vendor_id}</td>
                                                <td className="text-start">{vendor.user.name}</td>
                                                <td className="text-start">{vendor.user.email}</td>
                                                <td className="text-center">
                                                    <div className="d-flex align-items-center justify-content-center">
                                                        <select
                                                            className={`form-select form-select-sm w-auto ${vendor.user.status === 'active' ? 'border-success' : 'border-danger'}`}
                                                            value={vendor.user.status}
                                                            disabled={statusUpdating[vendor.vendor_id]}
                                                            onChange={e => handleStatusChange(vendor, e.target.value)}
                                                        >
                                                            <option value="active">Active</option>
                                                            <option value="inactive">Inactive</option>
                                                        </select>
                                                        {statusUpdating[vendor.vendor_id] && (
                                                            <span className="spinner-border spinner-border-sm ms-2" role="status" aria-hidden="true"></span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td style={{ padding: "4px" }}>
                                                    <ul
                                                        className="list-inline mb-0 d-flex align-items-center"
                                                        style={{ gap: "4px" }}
                                                    >
                                                        <li className="list-inline-item">
                                                            <Link
                                                                to={`/vendordetails/${vendor.vendor_id}`}
                                                                className="btn btn-sm btn-light p-1"
                                                                style={{ lineHeight: 1, minWidth: 0 }}
                                                            >
                                                                <i
                                                                    className="ri-eye-line"
                                                                    style={{
                                                                        cursor: "pointer",
                                                                        fontSize: "16px",
                                                                    }}
                                                                ></i>
                                                            </Link>
                                                        </li>
                                                        <li className="list-inline-item">
                                                            <Link
                                                                to={`/vendoraddorupdate/${vendor.vendor_id}`}
                                                                className="btn btn-sm btn-light p-1"
                                                                style={{ lineHeight: 1, minWidth: 0 }}
                                                            >
                                                                <i
                                                                    className="ri-pencil-line"
                                                                    style={{
                                                                        cursor: "pointer",
                                                                        fontSize: "16px",
                                                                    }}
                                                                ></i>
                                                            </Link>
                                                        </li>
                                                        <li className="list-inline-item">
                                                            <button
                                                                onClick={() => handleDeleteClick(vendor)}
                                                                className="btn btn-sm btn-light p-1"
                                                                style={{
                                                                    border: "none",
                                                                    background: "none",
                                                                    padding: 0,
                                                                    lineHeight: 1,
                                                                    minWidth: 0,
                                                                }}
                                                                disabled={statusUpdating[vendor.vendor_id] || deleteLoading}
                                                            >
                                                                <i
                                                                    className="ri-delete-bin-line"
                                                                    style={{
                                                                        cursor: "pointer",
                                                                        fontSize: "16px",
                                                                        color: "#dc3545",
                                                                        verticalAlign: "middle",
                                                                    }}
                                                                ></i>
                                                            </button>
                                                        </li>
                                                    </ul>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {/* Pagination Controls */}
                            <nav className="d-flex justify-content-center mt-4">
                                <ul className="pagination">
                                    <li className={`page-item${page === 1 ? ' disabled' : ''}`}>
                                        <button className="page-link" onClick={() => handlePageChange(page - 1)} disabled={page === 1}>&laquo; Prev</button>
                                    </li>
                                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((pg) => (
                                        <li key={pg} className={`page-item${pg === page ? ' active' : ''}`}>
                                            <button className="page-link" onClick={() => handlePageChange(pg)}>{pg}</button>
                                        </li>
                                    ))}
                                    <li className={`page-item${page === totalPages ? ' disabled' : ''}`}>
                                        <button className="page-link" onClick={() => handlePageChange(page + 1)} disabled={page === totalPages}>Next &raquo;</button>
                                    </li>
                                </ul>
                            </nav>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Delete Modal */}
            {showDeleteModal && (
                <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1" role="dialog">
                    <div className="modal-dialog" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Delete Vendor</h5>
                                <button type="button" className="btn-close" onClick={() => !deleteLoading && setShowDeleteModal(false)} aria-label="Close" disabled={deleteLoading}></button>
                            </div>
                            <div className="modal-body">
                                <p>Are you sure you want to delete this vendor?</p>
                            </div>
                            <div className="modal-footer">
                                <button
                                    className="btn btn-primary"
                                    onClick={() => setShowDeleteModal(false)}
                                    disabled={deleteLoading}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="btn btn-danger"
                                    onClick={handleDeleteConfirm}
                                    disabled={deleteLoading}
                                >
                                    {deleteLoading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            Deleting...
                                        </>
                                    ) : (
                                        'Delete'
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}